/**
 * Consistent card wrapper for every chart and data table on the analytics
 * surface. Standardizes title/subtitle typography, loading/empty/error states,
 * optional action slot (filter toggle, download, etc.), and spacing.
 *
 * Use this instead of Card+CardHeader+CardTitle directly — it removes 30 lines
 * of boilerplate from every tab file.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { cn } from '../../../../lib/cn';
import { EmptyState } from './EmptyState';

export interface ChartCardProps {
  title: string;
  subtitle?: string;
  className?: string;
  headerAction?: React.ReactNode;
  loading?: boolean;
  error?: string | null;
  isEmpty?: boolean;
  emptyMessage?: string;
  onRetry?: () => void;
  children: React.ReactNode;
}

export const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  className,
  headerAction,
  loading = false,
  error = null,
  isEmpty = false,
  emptyMessage = 'No data in this range yet.',
  onRetry,
  children,
}) => {
  return (
    <Card
      className={cn(
        'bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow',
        className,
      )}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
        <div className="min-w-0">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-gray-700">
            {title}
          </CardTitle>
          {subtitle && (
            <p className="mt-1 text-xs text-gray-500">{subtitle}</p>
          )}
        </div>
        {headerAction && <div className="shrink-0">{headerAction}</div>}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-48 w-full animate-pulse rounded-md bg-gray-100" />
        ) : error ? (
          <EmptyState
            variant="error"
            title="Couldn't load"
            message={error}
            onRetry={onRetry}
          />
        ) : isEmpty ? (
          <EmptyState variant="empty" message={emptyMessage} />
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
};
